import { Link, useParams } from 'react-router-dom';

import { useProject, useProjectOpenPrs } from '@/hooks/use-projects';
import { formatRelativeTime } from '@/utils/format-time';
import { truncateTitle } from '@/utils/truncate';

import type { ProjectPr } from '@/types/project';

const PR_TITLE_MAX = 120;

function StatusBadge({ status }: { status: ProjectPr['status'] }) {
  const styles: Record<ProjectPr['status'], string> = {
    OPEN: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    CLOSED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    MERGED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${styles[status]}`}
      aria-label={`Status: ${status.toLowerCase()}`}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function PrItem({ pr, projectKey }: { pr: ProjectPr; projectKey: string }) {
  return (
    <li>
      <Link
        to={`/projects/${encodeURIComponent(projectKey)}/repos/${encodeURIComponent(pr.repositoryName)}/prs/${pr.prId}`}
        className="block rounded-lg border border-border p-3 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-content">{truncateTitle(pr.title, PR_TITLE_MAX)}</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-content-secondary">
              <span>{pr.repositoryName}</span>
              <span>by {pr.authorDisplayName ?? pr.authorId}</span>
              <span>{formatRelativeTime(pr.lastActivityDate)}</span>
              {pr.approvalCount != null && pr.approvalCount > 0 && (
                <span aria-label={`${pr.approvalCount} approval${pr.approvalCount === 1 ? '' : 's'}`}>
                  👍 {pr.approvalCount}
                </span>
              )}
            </div>
          </div>
          <StatusBadge status={pr.status} />
        </div>
      </Link>
    </li>
  );
}

function PrListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading pull requests" className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-lg border border-border p-3">
          <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="mt-2 h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
}

export function ProjectActivityPage() {
  const { projectKey } = useParams<{ projectKey: string }>();
  const key = projectKey ?? '';

  const { data: project, isLoading: projectLoading } = useProject(key);

  const {
    data: prs,
    isLoading: prsLoading,
    isError: prsError,
    refetch: refetchPrs,
  } = useProjectOpenPrs(key);

  return (
    <main id="main-content" className="mx-auto max-w-4xl">
      {/* Project Header */}
      {projectLoading ? (
        <div aria-busy="true" className="animate-pulse mb-6">
          <div className="h-7 w-64 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="mt-2 h-4 w-32 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ) : (
        project && (
          <header className="mb-6">
            <h1 className="text-2xl font-bold text-content">{project.displayName}</h1>
            <p className="mt-1 text-sm text-content-secondary">
              Open pull requests across this project
            </p>
          </header>
        )
      )}

      {/* PR content */}
      {prsLoading && <PrListSkeleton />}
      {prsError && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">Failed to load pull requests.</p>
          <button
            onClick={() => refetchPrs()}
            className="mt-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Retry
          </button>
        </div>
      )}
      {!prsLoading && !prsError && prs && (
        <>
          {prs.length === 0 ? (
            <div className="rounded-lg border border-border p-6 text-center">
              <p className="text-sm text-content-secondary">No open pull requests for this project.</p>
            </div>
          ) : (
            <ul className="space-y-2" aria-label="Open pull requests">
              {prs.map((pr) => (
                <PrItem key={`${pr.repositoryName}#${pr.prId}`} pr={pr} projectKey={key} />
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}
